/**
 * @file NotificationContext.tsx
 * @description Real-time Notification Context for Venue Operators, Kitchen Staff, and Waiters.
 * 
 * CORE CAPABILITIES:
 * 1. Supabase Realtime Stream:
 *    Listens to INSERT/UPDATE/DELETE events on `venue_notifications` filtered by `venue_id`.
 * 2. Web Audio API Chime Synthesizer:
 *    Synthesizes sine wave audio chimes in real-time based on notification priority
 *    (urgent = 3-tone high chord, high = 2-tone, medium = gentle harmonic).
 * 3. Granular Preferences:
 *    Allows operators to mute specific alert categories (e.g. system, campaign)
 *    while preserving kitchen and order alerts.
 * 
 * DEBUGGING TIP: If sound fails to play in modern browsers, remember that the Web Audio API
 * requires an initial user interaction (click/tap) on the page before audio playback is permitted.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useVenues } from '../hooks/useVenues';
import { 
  VenueNotification, 
  NotificationCategory, 
  NotificationPriority, 
  NotificationPreferences, 
  CategoryPreference 
} from '../types/notifications';

const LOCAL_STORAGE_PREFS_KEY = 'tablegreet_venue_admin_notif_prefs_v1';

const DEFAULT_PREFERENCES: NotificationPreferences = {
  soundEnabled: true,
  volume: 0.6,
  desktopPushEnabled: false,
  categories: {
    order: { inApp: true, push: true, email: true },
    kitchen: { inApp: true, push: true, email: false },
    table: { inApp: true, push: true, email: false },
    system: { inApp: true, push: false, email: true },
    billing: { inApp: true, push: false, email: true },
    security: { inApp: true, push: true, email: true },
    campaign: { inApp: true, push: false, email: false }
  }
};

interface NotificationContextType {
  notifications: VenueNotification[];
  unreadCount: number;
  starredCount: number;
  urgentCount: number;
  preferences: NotificationPreferences;
  markAsRead: (id: string) => void;
  markAsUnread: (id: string) => void;
  markAllAsRead: () => void;
  toggleStarred: (id: string) => void;
  deleteNotification: (id: string) => void;
  deleteMultiple: (ids: string[]) => void;
  markMultipleAsRead: (ids: string[]) => void;
  starMultiple: (ids: string[]) => void;
  clearRead: () => void;
  clearAll: () => void;
  addNotification: (item: Omit<VenueNotification, 'id' | 'timestamp' | 'read' | 'starred'>) => void;
  addTestNotification: () => void;
  updatePreferences: (updates: Partial<NotificationPreferences>) => void;
  updateCategoryPreference: (category: NotificationCategory, key: keyof CategoryPreference, val: boolean) => void;
  playChime: (priority?: NotificationPriority) => void;
  testChime: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const playSyntheticChime = (volume: number, priority: NotificationPriority = 'medium') => {
  if (volume <= 0) return;
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const now = ctx.currentTime;

    const freqs = priority === 'urgent' 
      ? [880, 1174, 1318] 
      : priority === 'high' 
      ? [587, 880] 
      : [523.25, 659.25];
    const duration = 0.14;

    freqs.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + idx * duration);

      gain.gain.setValueAtTime(0.001, now + idx * duration);
      gain.gain.exponentialRampToValueAtTime(Math.min(volume, 1), now + idx * duration + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + idx * duration + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + idx * duration);
      osc.stop(now + idx * duration + duration);
    });
  } catch (err) {
    console.warn('Could not play web audio chime:', err);
  }
};

const mapDbRowToNotif = (row: any): VenueNotification => ({
  id: row.id,
  title: row.title,
  message: row.message,
  category: (row.category || 'system') as NotificationCategory,
  priority: (row.priority || 'medium') as NotificationPriority,
  read: Boolean(row.read),
  starred: Boolean(row.starred),
  timestamp: row.created_at || new Date().toISOString(),
  actionUrl: row.action_url || undefined,
  metadata: row.metadata || undefined,
});

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeVenue } = useVenues();
  const [notifications, setNotifications] = useState<VenueNotification[]>([]);

  const [preferences, setPreferences] = useState<NotificationPreferences>(() => {
    try {
      const backendPrefs = activeVenue?.theme_settings?.notification_preferences;
      if (backendPrefs) {
        return { ...DEFAULT_PREFERENCES, ...backendPrefs };
      }
      const saved = localStorage.getItem(LOCAL_STORAGE_PREFS_KEY) || localStorage.getItem('tg_venue_notifications_prefs');
      if (saved) return { ...DEFAULT_PREFERENCES, ...JSON.parse(saved) };
    } catch (e) {
      console.error('Failed to parse saved notification preferences', e);
    }
    return DEFAULT_PREFERENCES;
  });

  // Hydrate preferences from venue backend theme_settings if available
  useEffect(() => {
    const backendPrefs = activeVenue?.theme_settings?.notification_preferences;
    if (backendPrefs) {
      setPreferences(prev => ({
        ...prev,
        ...backendPrefs
      }));
    }
  }, [activeVenue?.id, activeVenue?.theme_settings?.notification_preferences]);

  // Fetch venue notifications & subscribe to Supabase Realtime when activeVenue changes
  useEffect(() => {
    if (!activeVenue) {
      setNotifications([]);
      return;
    }

    let isMounted = true;

    // Hydrates initial venue notifications from unified bootstrap state or queries database directly
    const loadNotifications = async () => {
      try {
        // Reads pre-seeded notifications from unified bootstrap state
        const cachedNotifsRaw = sessionStorage.getItem(`venue_notifications_${activeVenue.id}`);
        if (cachedNotifsRaw) {
          try {
            const cachedNotifs = JSON.parse(cachedNotifsRaw);
            if (Array.isArray(cachedNotifs) && cachedNotifs.length > 0 && isMounted) {
              setNotifications(cachedNotifs.map(mapDbRowToNotif));
              return;
            }
          } catch {
            // Suppresses cached notification parse errors and continues to database query
          }
        }

        const { data, error } = await supabase
          .from('venue_notifications')
          .select('*')
          .eq('venue_id', activeVenue.id)
          .order('created_at', { ascending: false })
          .limit(100);

        if (!error && data) {
          if (isMounted) {
            setNotifications(data.map(mapDbRowToNotif));
          }
        }
      } catch (err) {
        console.error('Error fetching venue notifications from DB:', err);
      }
    };

    loadNotifications();

    // Supabase Realtime Subscription
    const channel = supabase.channel(`venue_notifications:${activeVenue.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'venue_notifications',
          filter: `venue_id=eq.${activeVenue.id}`,
        },
        (payload) => {
          if (!isMounted) return;
          const newNotif = mapDbRowToNotif(payload.new);
          setNotifications(prev => [newNotif, ...prev.filter(n => n.id !== newNotif.id)]);
          
          if (preferences.soundEnabled) {
            playSyntheticChime(preferences.volume, newNotif.priority);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'venue_notifications',
          filter: `venue_id=eq.${activeVenue.id}`,
        },
        (payload) => {
          if (!isMounted) return;
          const updated = mapDbRowToNotif(payload.new);
          setNotifications(prev => prev.map(n => n.id === updated.id ? updated : n));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'venue_notifications',
          filter: `venue_id=eq.${activeVenue.id}`,
        },
        (payload) => {
          if (!isMounted) return;
          setNotifications(prev => prev.filter(n => n.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, [activeVenue?.id, preferences.soundEnabled, preferences.volume]);

  // Persist preferences to localStorage (instant client cache) and Supabase backend (venues.theme_settings)
  useEffect(() => {
    try {
      const serialized = JSON.stringify(preferences);
      localStorage.setItem(LOCAL_STORAGE_PREFS_KEY, serialized);
      localStorage.setItem('tg_venue_notifications_prefs', serialized);
    } catch (e) {
      console.error('Error saving notification preferences', e);
    }

    if (!activeVenue?.id) return;
    const timer = setTimeout(() => {
      const currentPrefs = activeVenue.theme_settings?.notification_preferences;
      if (JSON.stringify(currentPrefs) !== JSON.stringify(preferences)) {
        activeVenue.theme_settings = {
          ...activeVenue.theme_settings,
          notification_preferences: preferences
        };
        supabase
          .from('venues')
          .update({
            theme_settings: activeVenue.theme_settings
          })
          .eq('id', activeVenue.id)
          .then(() => {});
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [preferences, activeVenue]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const starredCount = notifications.filter(n => n.starred).length;
  const urgentCount = notifications.filter(n => n.priority === 'urgent' || n.priority === 'high').length;

  const playChime = useCallback((priority: NotificationPriority = 'medium') => {
    if (preferences.soundEnabled) {
      playSyntheticChime(preferences.volume, priority);
    }
  }, [preferences.soundEnabled, preferences.volume]);

  const testChime = useCallback(() => {
    playSyntheticChime(preferences.volume, 'high');
  }, [preferences.volume]);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    if (activeVenue) {
      supabase.from('venue_notifications').update({ read: true }).eq('id', id).then(() => {});
    }
  }, [activeVenue]);

  const markAsUnread = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: false } : n));
    if (activeVenue) {
      supabase.from('venue_notifications').update({ read: false }).eq('id', id).then(() => {});
    }
  }, [activeVenue]);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    if (activeVenue) {
      supabase.from('venue_notifications').update({ read: true }).eq('venue_id', activeVenue.id).then(() => {});
    }
  }, [activeVenue]);

  const toggleStarred = useCallback((id: string) => {
    setNotifications(prev => {
      const target = prev.find(n => n.id === id);
      const nextStarred = !target?.starred;
      if (activeVenue && target) {
        supabase.from('venue_notifications').update({ starred: nextStarred }).eq('id', id).then(() => {});
      }
      return prev.map(n => n.id === id ? { ...n, starred: nextStarred } : n);
    });
  }, [activeVenue]);

  const deleteNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (activeVenue) {
      supabase.from('venue_notifications').delete().eq('id', id).then(() => {});
    }
  }, [activeVenue]);

  const deleteMultiple = useCallback((ids: string[]) => {
    setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
    if (activeVenue && ids.length > 0) {
      supabase.from('venue_notifications').delete().in('id', ids).then(() => {});
    }
  }, [activeVenue]);

  const markMultipleAsRead = useCallback((ids: string[]) => {
    setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n));
    if (activeVenue && ids.length > 0) {
      supabase.from('venue_notifications').update({ read: true }).in('id', ids).then(() => {});
    }
  }, [activeVenue]);

  const starMultiple = useCallback((ids: string[]) => {
    setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, starred: true } : n));
    if (activeVenue && ids.length > 0) {
      supabase.from('venue_notifications').update({ starred: true }).in('id', ids).then(() => {});
    }
  }, [activeVenue]);

  const clearRead = useCallback(() => {
    setNotifications(prev => {
      const readIds = prev.filter(n => n.read).map(n => n.id);
      if (activeVenue && readIds.length > 0) {
        supabase.from('venue_notifications').delete().in('id', readIds).then(() => {});
      }
      return prev.filter(n => !n.read);
    });
  }, [activeVenue]);

  const clearAll = useCallback(() => {
    setNotifications([]);
    if (activeVenue) {
      supabase.from('venue_notifications').delete().eq('venue_id', activeVenue.id).then(() => {});
    }
  }, [activeVenue]);

  const addNotification = useCallback((item: Omit<VenueNotification, 'id' | 'timestamp' | 'read' | 'starred'>) => {
    const newNotif: VenueNotification = {
      ...item,
      id: `notif-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      timestamp: new Date().toISOString(),
      read: false,
      starred: false
    };

    setNotifications(prev => [newNotif, ...prev]);

    if (activeVenue) {
      supabase.from('venue_notifications').insert({
        venue_id: activeVenue.id,
        category: item.category,
        priority: item.priority,
        title: item.title,
        message: item.message,
        action_url: item.actionUrl,
        metadata: item.metadata || {},
      }).then(() => {});
    }

    const catPref = preferences.categories[item.category];
    if (catPref?.inApp) {
      playChime(item.priority);
    }
  }, [activeVenue, preferences, playChime]);

  const addTestNotification = useCallback(() => {
    const testTemplates: Array<Omit<VenueNotification, 'id' | 'timestamp' | 'read' | 'starred'>> = [
      {
        title: 'New Order Received #1049',
        message: 'Table 7 ordered 1x Chef Signature Steak and 1x Red Wine.',
        category: 'order',
        priority: 'urgent',
        actionUrl: '/admin/kitchen',
        actionLabel: 'Open KDS'
      },
      {
        title: 'Table 3 Called Waiter',
        message: 'Guest requested napkin replenishment and extra cutlery.',
        category: 'table',
        priority: 'high',
        actionUrl: '/admin/kitchen',
        actionLabel: 'View Floor Plan'
      },
      {
        title: 'System Backup Completed',
        message: 'Menu database and media assets backed up securely to Cloud Storage.',
        category: 'system',
        priority: 'low',
        actionUrl: '/admin/settings',
        actionLabel: 'View System Settings'
      }
    ];

    const randomItem = testTemplates[Math.floor(Math.random() * testTemplates.length)];
    addNotification(randomItem);
  }, [addNotification]);

  const updatePreferences = useCallback((updates: Partial<NotificationPreferences>) => {
    setPreferences(prev => ({ ...prev, ...updates }));
  }, []);

  const updateCategoryPreference = useCallback((
    category: NotificationCategory, 
    key: keyof CategoryPreference, 
    val: boolean
  ) => {
    setPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: {
          ...prev.categories[category],
          [key]: val
        }
      }
    }));
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        starredCount,
        urgentCount,
        preferences,
        markAsRead,
        markAsUnread,
        markAllAsRead,
        toggleStarred,
        deleteNotification,
        deleteMultiple,
        markMultipleAsRead,
        starMultiple,
        clearRead,
        clearAll,
        addNotification,
        addTestNotification,
        updatePreferences,
        updateCategoryPreference,
        playChime,
        testChime
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
